import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from '../api/http';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Paper
} from '@mui/material';

// Form-Schema für Passwortänderung
const passwordChangeSchema = Yup.object({
  currentPassword: Yup.string().required('Aktuelles Passwort ist erforderlich'),
  // Regeln identisch zur Prüfung im Backend (server/src/utils/validation.ts)
  newPassword: Yup.string()
    .required('Neues Passwort ist erforderlich')
    .min(10, 'Das Passwort muss mindestens 10 Zeichen lang sein')
    .matches(/[A-Za-z]/, 'Das Passwort muss mindestens einen Buchstaben enthalten')
    .matches(/[0-9]/, 'Das Passwort muss mindestens eine Zahl enthalten')
    .notOneOf(
      [Yup.ref('currentPassword')],
      'Das neue Passwort muss sich vom aktuellen unterscheiden'
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwörter müssen übereinstimmen')
    .required('Passwortbestätigung ist erforderlich')
});

const ChangePassword = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: passwordChangeSchema,
    onSubmit: async (values) => {
      try {
        setError(null);
        setSuccess(null);

        await axios.patch('/api/users/change-password', {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword
        });

        setSuccess('Passwort erfolgreich geändert');
        formik.resetForm();
      } catch (err: any) {
        console.error('Fehler beim Ändern des Passworts:', err);
        setError(err.response?.data?.message || 'Ein unerwarteter Fehler ist aufgetreten');
      }
    }
  });

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Passwort ändern
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                id="currentPassword"
                name="currentPassword"
                label="Aktuelles Passwort"
                type="password"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
                helperText={formik.touched.currentPassword && formik.errors.currentPassword}
                required
              />

              <TextField
                fullWidth
                id="newPassword"
                name="newPassword"
                label="Neues Passwort"
                type="password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                helperText={formik.touched.newPassword && formik.errors.newPassword}
                required
              />

              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Passwort bestätigen"
                type="password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                required
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
              >
                Passwort ändern
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChangePassword; 